import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Download, Printer, Award } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CertificateView() {
  const { certId } = useParams();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const certRef = useRef(null);

  useEffect(() => { fetchCert(); }, [certId]);

  const fetchCert = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/certificates/${certId}`);
      setCert(res.data);
    } catch (e) {
      console.error('Failed to load certificate');
    } finally { setLoading(false); }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!cert) {
    return (
      <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center">
        <p className="text-gray-400">Certificate not found</p>
      </div>
    );
  }

  const issuedDate = new Date(cert.issued_at).toLocaleDateString('en-US', { 
    year: 'numeric', month: 'long', day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-[#0d0d1a]">
      {/* Controls - hidden when printing */}
      <div className="print:hidden max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/my-certificates">
          <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Certificates
          </Button>
        </Link>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handlePrint} className="border-gray-600 text-gray-300" data-testid="print-cert-btn">
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
        </div>
      </div>

      {/* Certificate */}
      <div className="max-w-4xl mx-auto px-4 pb-12">
        <div 
          ref={certRef}
          className="bg-white rounded-lg shadow-2xl overflow-hidden print:shadow-none print:rounded-none"
          style={{ aspectRatio: '1.414/1' }}
          data-testid="certificate-document"
        >
          {/* Outer border frame */}
          <div className="h-full p-6 sm:p-10">
            <div className="h-full border-4 border-double border-amber-600 rounded-sm p-6 sm:p-10 flex flex-col items-center justify-between relative overflow-hidden">
              
              {/* Decorative corner elements */}
              <div className="absolute top-3 left-3 w-8 h-8 border-t-2 border-l-2 border-amber-500 rounded-tl-sm" />
              <div className="absolute top-3 right-3 w-8 h-8 border-t-2 border-r-2 border-amber-500 rounded-tr-sm" />
              <div className="absolute bottom-3 left-3 w-8 h-8 border-b-2 border-l-2 border-amber-500 rounded-bl-sm" />
              <div className="absolute bottom-3 right-3 w-8 h-8 border-b-2 border-r-2 border-amber-500 rounded-br-sm" />
              
              {/* Watermark */}
              <div className="absolute inset-0 flex items-center justify-center opacity-[0.03] pointer-events-none">
                <Award className="w-96 h-96 text-amber-700" />
              </div>

              {/* Top Section */}
              <div className="text-center z-10">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <div className="w-px h-8 bg-amber-500" />
                  <Award className="w-8 h-8 text-amber-600" />
                  <div className="w-px h-8 bg-amber-500" />
                </div>
                <p className="text-xs sm:text-sm tracking-[0.35em] text-amber-700 uppercase font-semibold mb-1">
                  We Masterminds
                </p>
                <h1 className="text-2xl sm:text-4xl font-serif font-bold text-gray-800 tracking-wide">
                  Certificate of Completion
                </h1>
                <div className="w-32 h-px bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto mt-4" />
              </div>

              {/* Middle Section */}
              <div className="text-center z-10 -mt-4">
                <p className="text-sm text-gray-500 mb-3">This certifies that</p>
                <h2 className="text-3xl sm:text-5xl font-serif font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  {cert.user_name}
                </h2>
                <div className="w-48 h-px bg-gray-300 mx-auto mb-4" />
                <p className="text-sm text-gray-500 mb-2">has successfully completed the course</p>
                <h3 className="text-xl sm:text-2xl font-serif font-semibold text-gray-800 italic mb-1">
                  "{cert.course_title}"
                </h3>
              </div>

              {/* Bottom Section */}
              <div className="w-full z-10">
                <div className="flex justify-between items-end px-4 sm:px-12">
                  <div className="text-center">
                    <div className="w-36 border-b border-gray-400 mb-1" />
                    <p className="text-xs text-gray-500">{issuedDate}</p>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider mt-0.5">Date of Issue</p>
                  </div>
                  <div className="text-center">
                    <div className="w-36 border-b border-gray-400 mb-1" />
                    <p className="text-xs text-gray-500 font-mono">{cert.certificate_number}</p>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider mt-0.5">Certificate ID</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:rounded-none { border-radius: 0 !important; }
        }
      `}</style>
    </div>
  );
}
